import React, { useCallback, useEffect, useState } from "react";
import SiteLayout from "../../components/layout/SiteLayout";
import PageIntro from "../../components/ui/PageIntro";
import {
  FaArrowDown,
  FaArrowLeft,
  FaArrowRight,
  FaArrowUp,
  FaClock,
  FaFlagCheckered,
  FaKeyboard,
  FaRedoAlt,
  FaRoute,
  FaTrophy,
} from "react-icons/fa";
import "./random-generator.css";

const DIRECTION_DELTAS = {
  up: { dr: -1, dc: 0 },
  down: { dr: 1, dc: 0 },
  left: { dr: 0, dc: -1 },
  right: { dr: 0, dc: 1 },
};

const KEY_TO_DIRECTION = {
  ArrowUp: "up",
  w: "up",
  W: "up",
  ArrowDown: "down",
  s: "down",
  S: "down",
  ArrowLeft: "left",
  a: "left",
  A: "left",
  ArrowRight: "right",
  d: "right",
  D: "right",
};

const DIFFICULTY_PRESETS = [
  { label: "Compact", size: 7, note: "Fast rounds" },
  { label: "Standard", size: 11, note: "Balanced play" },
  { label: "Deep", size: 15, note: "Longer routes" },
  { label: "Labyrinth", size: 19, note: "Big board" },
];

const DEFAULT_SIZE = 11;

function getRandomNumber(limit) {
  return Math.floor(Math.random() * limit);
}

function shuffleDirections(directions) {
  const next = [...directions];

  for (let i = next.length - 1; i > 0; i -= 1) {
    const swapIndex = getRandomNumber(i + 1);
    [next[i], next[swapIndex]] = [next[swapIndex], next[i]];
  }

  return next;
}

function createMaze(size) {
  const gridSize = Math.max(5, size) * 2 + 1;
  const grid = Array.from({ length: gridSize }, () => Array(gridSize).fill(1));

  return {
    grid,
    start: { r: 1, c: 1 },
    finish: { r: gridSize - 2, c: gridSize - 2 },
    gridSize,
  };
}

function randomDfs(size) {
  const maze = createMaze(size);
  const { grid } = maze;
  const stack = [{ r: 1, c: 1 }];
  const visited = new Set(["1_1"]);
  const directions = [
    { dr: 0, dc: 2 },
    { dr: 2, dc: 0 },
    { dr: 0, dc: -2 },
    { dr: -2, dc: 0 },
  ];

  grid[1][1] = 0;

  while (stack.length > 0) {
    const current = stack[stack.length - 1];
    const neighbors = shuffleDirections(directions).filter(({ dr, dc }) => {
      const nextRow = current.r + dr;
      const nextCol = current.c + dc;

      if (nextRow <= 0 || nextRow >= grid.length - 1) return false;
      if (nextCol <= 0 || nextCol >= grid.length - 1) return false;

      return !visited.has(`${nextRow}_${nextCol}`);
    });

    if (neighbors.length === 0) {
      stack.pop();
      continue;
    }

    const { dr, dc } = neighbors[getRandomNumber(neighbors.length)];
    const nextRow = current.r + dr;
    const nextCol = current.c + dc;
    const betweenRow = current.r + dr / 2;
    const betweenCol = current.c + dc / 2;

    grid[betweenRow][betweenCol] = 0;
    grid[nextRow][nextCol] = 0;
    visited.add(`${nextRow}_${nextCol}`);
    stack.push({ r: nextRow, c: nextCol });
  }

  grid[maze.finish.r][maze.finish.c] = 0;

  if (!isMazeSolvable(grid, maze.start, maze.finish)) {
    carveFallbackRoute(grid, maze.start, maze.finish);
  }

  return maze;
}

function isMazeSolvable(grid, start, finish) {
  const queue = [start];
  const visited = new Set([`${start.r}_${start.c}`]);
  const directions = [
    { dr: 1, dc: 0 },
    { dr: -1, dc: 0 },
    { dr: 0, dc: 1 },
    { dr: 0, dc: -1 },
  ];

  while (queue.length > 0) {
    const current = queue.shift();

    if (current.r === finish.r && current.c === finish.c) {
      return true;
    }

    directions.forEach(({ dr, dc }) => {
      const nextRow = current.r + dr;
      const nextCol = current.c + dc;
      const nextRowData = grid[nextRow];

      if (!nextRowData || nextRowData[nextCol] !== 0) return;

      const key = `${nextRow}_${nextCol}`;
      if (visited.has(key)) return;

      visited.add(key);
      queue.push({ r: nextRow, c: nextCol });
    });
  }

  return false;
}

function carveFallbackRoute(grid, start, finish) {
  let row = start.r;
  let col = start.c;

  grid[row][col] = 0;

  while (col !== finish.c) {
    col += col < finish.c ? 1 : -1;
    grid[row][col] = 0;
  }

  while (row !== finish.r) {
    row += row < finish.r ? 1 : -1;
    grid[row][col] = 0;
  }
}

function formatSeconds(totalSeconds) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

export default function RandomGenerator() {
  const [maze, setMaze] = useState(() => randomDfs(DEFAULT_SIZE));
  const [player, setPlayer] = useState({ r: 1, c: 1 });
  const [trail, setTrail] = useState([{ r: 1, c: 1 }]);
  const [steps, setSteps] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [selectedSize, setSelectedSize] = useState(DEFAULT_SIZE);

  const startNewMaze = (size = selectedSize) => {
    const nextMaze = randomDfs(size);

    setSelectedSize(size);
    setMaze(nextMaze);
    setPlayer(nextMaze.start);
    setTrail([nextMaze.start]);
    setSteps(0);
    setSeconds(0);
    setTimerRunning(false);
    setCompleted(false);
  };

  const resetRun = () => {
    setPlayer(maze.start);
    setTrail([maze.start]);
    setSteps(0);
    setSeconds(0);
    setTimerRunning(false);
    setCompleted(false);
  };

  const handleMove = useCallback(
    (direction) => {
      if (completed) return;

      const delta = DIRECTION_DELTAS[direction];
      if (!delta) return;

      const nextRow = player.r + delta.dr;
      const nextCol = player.c + delta.dc;
      const nextRowData = maze.grid[nextRow];

      if (!nextRowData || nextRowData[nextCol] === undefined) return;
      if (nextRowData[nextCol] === 1) return;

      const nextPlayer = { r: nextRow, c: nextCol };
      const reachedFinish =
        nextRow === maze.finish.r && nextCol === maze.finish.c;

      setPlayer(nextPlayer);
      setTrail((currentTrail) => [...currentTrail, nextPlayer]);
      setSteps((currentSteps) => currentSteps + 1);
      setTimerRunning(true);

      if (reachedFinish) {
        setCompleted(true);
        setTimerRunning(false);
      }
    },
    [completed, maze, player]
  );

  useEffect(() => {
    const onKeyDown = (event) => {
      const direction = KEY_TO_DIRECTION[event.key];
      if (!direction) return;

      event.preventDefault();
      handleMove(direction);
    };

    window.addEventListener("keydown", onKeyDown);

    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handleMove]);

  useEffect(() => {
    if (!timerRunning || completed) return undefined;

    const timer = window.setInterval(() => {
      setSeconds((currentSeconds) => currentSeconds + 1);
    }, 1000);

    return () => window.clearInterval(timer);
  }, [completed, timerRunning]);

  const trailSet = new Set(trail.map(({ r, c }) => `${r}_${c}`));
  const boardStyle = {
    gridTemplateColumns: `repeat(${maze.grid.length}, minmax(0, 1fr))`,
    gridTemplateRows: `repeat(${maze.grid.length}, minmax(0, 1fr))`,
  };

    return (
      <SiteLayout>
      <main className="page-shell maze-game-page home-v3-shell">
        <PageIntro
          eyebrow="Technology / Maze Game"
          title="Find the exit."
          description="A playable DFS maze. Use the arrows, WASD, or on-screen controls to reach the checkered exit."
          visual={(
            <div className="maze-hero-panel home-v3-panel card-pad">
              <span className="home-v3-kicker">Run Status</span>
              <div className="maze-stat-grid">
                <div className="maze-stat"><div className="maze-stat-label">Moves</div><div className="maze-stat-value">{steps}</div></div>
                <div className="maze-stat"><div className="maze-stat-label">Time</div><div className="maze-stat-value">{formatSeconds(seconds)}</div></div>
                <div className="maze-stat"><div className="maze-stat-label">Board</div><div className="maze-stat-value">{maze.grid.length} x {maze.grid.length}</div></div>
                <div className="maze-stat"><div className="maze-stat-label">Status</div><div className={`maze-stat-value ${completed ? "is-complete" : ""}`}>{completed ? "Cleared" : timerRunning ? "Running" : "Ready"}</div></div>
              </div>
              {completed ? <div className="maze-complete-banner"><FaTrophy /><span>You found the exit. Build another maze and beat the clock.</span></div> : null}
            </div>
          )}
        >
          <div className="page-intro-actions maze-actions">
                <button
                  type="button"
                  className="home-v3-button is-primary"
                  onClick={() => startNewMaze(selectedSize)}
                >
                  New Maze <FaRoute />
                </button>
                <button type="button" className="home-v3-button is-secondary" onClick={resetRun}>
                  Reset Run <FaRedoAlt />
                </button>
          </div>
        </PageIntro>

        <section className="maze-stage">
          <div className="maze-board-shell home-v3-panel">
            <div className="maze-board-header">
              <div>
                <span className="home-v3-kicker">Board</span>
                <h2>The path I generated.</h2>
                <p>
                  The dark cells are walls. The glowing trail is your route so
                  far. The start is at the top-left corner and the exit sits in
                  the lower-right corner.
                </p>
              </div>
              <div className="maze-board-key">
                <span>
                  <span className="maze-key-swatch maze-key-swatch-path" />
                  Open path
                </span>
                <span>
                  <span className="maze-key-swatch maze-key-swatch-wall" />
                  Wall
                </span>
                <span>
                  <span className="maze-key-swatch maze-key-swatch-trail" />
                  Your trail
                </span>
              </div>
            </div>

            <div className="maze-board-wrap">
              <div className="maze-board" style={boardStyle} role="grid" aria-label="Maze board">
                {maze.grid.map((row, rowIndex) =>
                  row.map((cell, colIndex) => {
                    const isPlayer = player.r === rowIndex && player.c === colIndex;
                    const isStart = maze.start.r === rowIndex && maze.start.c === colIndex;
                    const isFinish = maze.finish.r === rowIndex && maze.finish.c === colIndex;
                    const isTrail = trailSet.has(`${rowIndex}_${colIndex}`);

                    const classNames = [
                      "maze-cell",
                      cell === 1 ? "maze-cell-wall" : "maze-cell-path",
                      isStart ? "maze-cell-start" : "",
                      isFinish ? "maze-cell-finish" : "",
                      isTrail ? "maze-cell-trail" : "",
                      isPlayer ? "maze-cell-player" : "",
                    ]
                      .filter(Boolean)
                      .join(" ");

                    let content = null;

                    if (isPlayer) {
                      content = <span className="maze-player-dot" />;
                    } else if (isStart) {
                      content = <span className="maze-marker">S</span>;
                    } else if (isFinish) {
                      content = <FaFlagCheckered />;
                    }

                    return (
                      <div
                        key={`${rowIndex}_${colIndex}`}
                        className={classNames}
                        aria-hidden="true"
                      >
                        {content}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          <aside className="maze-sidebar">
            <div className="home-v3-panel card-pad maze-sidebar-card">
              <span className="home-v3-kicker">Difficulty</span>
              <h2>Maze sizes I can switch.</h2>
              <p>
                I kept the generator lightweight, so the board size is the main
                difficulty dial.
              </p>
              <div className="maze-difficulty-list">
                {DIFFICULTY_PRESETS.map((preset) => (
                  <button
                    key={preset.label}
                    type="button"
                    className={`maze-difficulty ${selectedSize === preset.size ? "is-active" : ""}`}
                    onClick={() => startNewMaze(preset.size)}
                  >
                    <span>{preset.label}</span>
                    <small>
                      {preset.size} x {preset.size} cells
                    </small>
                    <em>{preset.note}</em>
                  </button>
                ))}
              </div>
            </div>

            <div className="home-v3-panel card-pad maze-sidebar-card">
              <span className="home-v3-kicker">Controls</span>
              <h2>How I move.</h2>
              <p>
                I use arrows or WASD on desktop. On mobile, the on-screen pad
                handles movement.
              </p>
              <div className="maze-pad" aria-label="Maze movement controls">
                <button type="button" className="maze-pad-btn maze-pad-top" onClick={() => handleMove("up")}>
                  <FaArrowUp />
                </button>
                <div className="maze-pad-row">
                  <button type="button" className="maze-pad-btn" onClick={() => handleMove("left")}>
                    <FaArrowLeft />
                  </button>
                  <button type="button" className="maze-pad-btn" onClick={() => handleMove("down")}>
                    <FaArrowDown />
                  </button>
                  <button type="button" className="maze-pad-btn" onClick={() => handleMove("right")}>
                    <FaArrowRight />
                  </button>
                </div>
              </div>
            </div>

            <div className="home-v3-panel card-pad maze-sidebar-card">
              <span className="home-v3-kicker">Notes</span>
              <h2>What this page does now.</h2>
              <div className="maze-note-list">
                <div className="maze-note">
                  <FaKeyboard />
                  <span>Keyboard support for arrows and WASD.</span>
                </div>
                <div className="maze-note">
                  <FaFlagCheckered />
                  <span>Clear end state when the exit is reached.</span>
                </div>
                <div className="maze-note">
                  <FaClock />
                  <span>Live timer and move counter while you play.</span>
                </div>
              </div>
            </div>
          </aside>
        </section>
      </main>
    </SiteLayout>
  );
}
